// ============================================================================
// L'ONGLET et LE SEGMENTÉ — deux contrôles qu'on confond, et qui ne disent pas la même chose.
//
// L'ONGLET bascule entre deux VUES du même écran. ⚠️ Ce n'est PAS de la navigation : si le choix
// change la route, ce sont des liens, et un `role="tablist"` y serait un mensonge d'arbre
// d'accessibilité — le lecteur d'écran annoncerait « onglet 2 sur 5 » pour une page qui change.
//
// LE SEGMENTÉ pose une VALEUR parmi 2 à 4, toutes visibles. Au-delà de 4, ce n'est plus un
// segmenté : c'est une liste qu'on ouvre (`Picker`).
//
// SOURCE : 10 barres d'onglets et 11 pistes segmentées de la maquette (2026-08-16).
// ============================================================================

export const TABS_BAR = {
  gap: 2,
  border: 'ink-200',
  /**
   * ⚠️ LA BARRE DÉFILE au lieu de passer à la ligne : un écran porte 11 onglets, et une barre qui se
   * replie sur deux lignes déplace tout le contenu de la page sous le curseur.
   */
  scrollX: true,
} as const;

/**
 * ⚠️ LE FILET ET L'ÉCART APPARTIENNENT AU CRAN (correctif 2026-08-17). Le commentaire du cran
 * `compact` disait déjà « posés DANS un en-tête déjà séparé du corps : sans filet » — mais le composant
 * posait `borderBottom` et 20 px de marge QUELLE QUE SOIT la taille. Le cran ne rendait donc pas ce
 * qu'il déclarait, et une fois la barre posée dans l'en-tête on voyait DEUX traits gris à 1 px d'écart.
 * Une intention écrite dans un commentaire mais absente des données ne s'applique jamais.
 */
export const TABS_ITEM = {
  md: { height: 40, padH: 15, fontSize: 13.5, rule: true, spacingBelow: 20 },
  /** Onglets posés DANS un en-tête déjà séparé du corps (fiche) : plus courts, SANS filet ni marge. */
  compact: { height: 38, padH: 13, fontSize: 13, rule: false, spacingBelow: 0 },
  /**
   * Les onglets de l'univers des Ventes, coiffé de son titre : ceux de `md`, plus près du contenu — chaque onglet
   * ouvre sur sa propre ligne de description, et 20 px l'en décolleraient (maquette du back-office).
   */
  universe: { height: 40, padH: 15, fontSize: 13.5, rule: true, spacingBelow: 14 },
  /**
   * Les onglets d’un univers coiffé d’un SURTITRE (« Statistiques · Stock ») : plus serrés, et sans écart propre — c’est
   * le bloc de l’univers qui porte l’espace jusqu’au titre de l’onglet (maquette du back-office).
   */
  strip: { height: 40, padH: 13, fontSize: 13.5, rule: true, spacingBelow: 0 },
} as const;
export type TabsSize = keyof typeof TABS_ITEM;

/**
 * ⚠️ LA GRAISSE NE CHANGE PAS À L'ACTIVATION (600 dans les deux états), contrairement à ce que la
 * charte portait. C'est délibéré : une graisse qui change modifie la LARGEUR du libellé, donc tous
 * les onglets à droite glissent à chaque clic. L'état se lit à l'encre et au trait, qui ne coûtent
 * aucun pixel.
 */
export const TABS_ITEM_WEIGHT = 600;

/** ⚠️ Le trait est RÉSERVÉ dès le repos (transparent) : l'activation ne décale jamais le texte. */
export const TABS_UNDERLINE = { height: 2, activeColor: 'teal-500' } as const;

export const TABS_INK = {
  idle: 'ink-500',
  active: 'teal-800',
  /**
   * ⚠️ SURVOL CONSERVÉ alors que la maquette n'en a pas. Elle croit en avoir un — un filtre global de
   * luminosité — mais appliqué à un bouton au fond TRANSPARENT il ne change qu'un texte gris de 4,5 %,
   * c'est-à-dire rien de perceptible. Un onglet sans survol ne dit pas qu'il est cliquable.
   */
  hover: 'ink-800',
} as const;

/** L'ICÔNE d'un onglet, posée avant son libellé — l'écart est celui du compteur (`TABS_BADGE.gap`). */
export const TABS_ICON = { size: 15, stroke: 1.9 } as const;

/**
 * COMPTEUR porté par un onglet.
 * ⚠️ Réutilise le cran `xs` de la pastille plutôt qu'une quatrième géométrie. Ce qui s'ajoute n'est
 * pas une taille mais `tabular-nums` : un compteur qui change de largeur en se rafraîchissant fait
 * bouger toute la barre.
 * ⚠️ `hideWhenZero` par défaut — mais un écran peut l'imposer : au journal des ventes, « 0 annulation »
 * est une INFORMATION, pas un vide.
 */
export const TABS_BADGE = { gap: 7, size: 'xs', numeric: true, hideWhenZero: true } as const;

export const TABS_MOTION = { transitionMs: 130 } as const;

// ── SEGMENTÉ ────────────────────────────────────────────────────────────────────────────────────

/**
 * PISTE À PASTILLE — la forme dominante (9 emplois sur 11).
 * ⚠️ La BORDURE est un ajout de la maquette sur la charte, et elle sert : sans elle, une piste grise
 * posée sur du papier n'a pas de bord — elle se lit comme une zone de texte désactivée, pas comme un
 * creux dans lequel une pastille glisse.
 */
export const SEG_TRACK = { pad: 3, gap: 3, radius: 'ctrl', bg: 'ink-100', border: 'ink-200' } as const;


export const SEG_ITEM = {
  sm: { height: 28, padH: 12, fontSize: 12 },
  md: { height: 30, padH: 12, fontSize: 12.5 },
} as const;
export type SegSize = keyof typeof SEG_ITEM;

export const SEG_ITEM_STYLE = {
  radius: 'xs',
  weight: 600,
  idleInk: 'ink-500',
  activeBg: 'paper',
  /**
   * ⚠️ Encre de MARQUE et non d'encre neutre : l'actif reste de la famille du produit, et en thème
   * sombre ce rôle est redéfini en menthe claire — il suit donc le mode, ce qu'un hex figé ne fait pas.
   */
  activeInk: 'teal-800',
  /** RELIEF, pas assombrissement : sur un fond déjà creusé, foncer l'actif l'enfonce au lieu de le sortir. */
  activeShadow: 'sm',
  hoverInk: 'ink-900',
  transitionMs: 130,
} as const;

/**
 * PISTE À POUCE GLISSANT — le sélecteur de période (Jour / Semaine / Mois / Exercice).
 * ⚠️ ICI L'ENCRE NE CHANGE PAS : les libellés restent tous de la même couleur, c'est le POUCE SEUL
 * qui dit l'état. C'est ce qui justifie de garder cette forme distincte — quand la valeur courante
 * n'est AUCUNE des quatre (des bornes de dates libres), le pouce s'efface, et le contrôle refuse de
 * désigner une valeur qui n'est pas en vigueur (§23.0). Une piste à pastille ne sait pas exprimer
 * ça : il y a toujours une pastille allumée.
 */
export const SEG_THUMB = {
  trackRadius: 'pill', trackBg: 'ink-100', trackBorder: 'ink-200', pad: 3,
  segWidth: 74, segHeight: 30, fontSize: 12.5, weight: 600, ink: 'ink-800',
  thumbBg: 'paper',
  transitionMs: 220, easing: 'cubic-bezier(.4,0,.2,1)',
  hiddenWhenUnset: true,
} as const;
